import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import { createFixedAsset, fetchBranchStaff, fetchFixedAssets, updateFixedAsset } from "../../../api/accounting";
import { fetchBranches } from "../../../api/users";
import { formatDate, formatTZS } from "../../../utils/format";
import { useConfirmDialog } from "../Common/ConfirmDialogContext";
import { DEFAULT_TABLE_CUSTOM_STYLES } from "../Common/credoraTableUtils";
import { formatApiError, runCrud, runLoad } from "../Common/credoraToast";
import { ACCOUNTING_SECTION } from "./accountingPlaceholders";
import AccountingIconActions from "./AccountingIconActions";
import BranchStaffSelect from "./BranchStaffSelect";
import { todayIsoDate, useLatestRef } from "./accountingUtils";

const STATUS_META = {
  active: { label: "Active", color: "success" },
  disposed: { label: "Disposed", color: "secondary" },
  fully_depreciated: { label: "Fully depreciated", color: "info" },
};

const FixedAssetsPage = () => {
  const { confirm } = useConfirmDialog();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [staff, setStaff] = useState([]);
  const [staffLoading, setStaffLoading] = useState(false);
  const [form, setForm] = useState({
    asset_tag: "",
    name: "",
    category: "",
    acquisition_date: todayIsoDate(),
    acquisition_cost: "",
    salvage_value: "0",
    useful_life_months: "60",
    location: "",
    custodian_user_id: "",
  });

  useEffect(() => {
    fetchBranches(true).then((list) => {
      setBranches(list || []);
      if (list?.length) setBranchId(String(list[0].id));
    });
  }, []);

  const load = useCallback(() => {
    if (!branchId) return Promise.resolve();
    setLoading(true);
    setError("");
    return runLoad(() => fetchFixedAssets(branchId).then(setRows), { errorMessage: "Failed to load fixed assets." })
      .catch((err) => setError(formatApiError(err, "Failed to load fixed assets.")))
      .finally(() => setLoading(false));
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  const loadRef = useLatestRef(load);

  const loadStaff = useCallback(async () => {
    if (!branchId) return;
    setStaffLoading(true);
    try {
      setStaff((await fetchBranchStaff(branchId)) || []);
    } catch {
      setStaff([]);
    } finally {
      setStaffLoading(false);
    }
  }, [branchId]);

  const openModal = () => {
    setModalOpen(true);
    loadStaff();
  };

  const dispose = useCallback(
    (row) => {
      confirm({
        title: `Dispose ${row.asset_tag}?`,
        confirmLabel: "Dispose",
        tone: "danger",
        successMessage: "Asset disposed.",
        onConfirm: async () => {
          await updateFixedAsset(row.id, { status: "disposed" });
          await loadRef.current();
        },
      });
    },
    [confirm, loadRef]
  );

  const columns = useMemo(
    () => [
      { name: "Tag", selector: (r) => r.asset_tag, width: "100px" },
      { name: "Name", selector: (r) => r.name, grow: 2 },
      { name: "Custodian", selector: (r) => r.custodian || "—", width: "140px" },
      { name: "Acquired", cell: (r) => formatDate(r.acquisition_date), width: "110px" },
      { name: "Cost", cell: (r) => formatTZS(r.acquisition_cost), right: true, width: "120px" },
      { name: "Book value", cell: (r) => formatTZS(r.book_value), right: true, width: "120px" },
      { name: "Monthly depr.", cell: (r) => formatTZS(r.monthly_depreciation), right: true, width: "120px" },
      {
        name: "Status",
        cell: (r) => {
          const m = STATUS_META[r.status] || { label: r.status, color: "secondary" };
          return <Badge color={m.color}>{m.label}</Badge>;
        },
        width: "130px",
      },
      {
        name: "",
        cell: (r) =>
          r.status === "active" ? (
            <AccountingIconActions
              actions={[
                {
                  icon: "trash",
                  label: "Dispose asset",
                  onClick: () => dispose(r),
                  variant: "danger",
                },
              ]}
            />
          ) : null,
        width: "56px",
        ignoreRowClick: true,
      },
    ],
    [dispose]
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await runCrud(
        () =>
          createFixedAsset({
            branch_id: branchId,
            asset_tag: form.asset_tag,
            name: form.name,
            category: form.category || null,
            acquisition_date: form.acquisition_date,
            acquisition_cost: Number(form.acquisition_cost),
            salvage_value: Number(form.salvage_value) || 0,
            useful_life_months: Number(form.useful_life_months) || 60,
            location: form.location || null,
            custodian_user_id: form.custodian_user_id || null,
          }),
        { successMessage: "Fixed asset registered." }
      );
      setModalOpen(false);
      load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs mainTitle={ACCOUNTING_SECTION} parent={ACCOUNTING_SECTION} title="Fixed Assets Register" />
      <div className="container-fluid">
        {error && <Alert color="danger">{error}</Alert>}
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Asset register</h5>
            <div className="d-flex flex-wrap align-items-center gap-2">
              <Input type="select" bsSize="sm" style={{ width: "160px" }} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </Input>
              <Button color="primary" size="sm" onClick={openModal}>Register asset</Button>
            </div>
          </div>
          <div className="card-body p-0">
            {loading ? <div className="text-center py-5"><Spinner color="primary" /></div> : (
              <DataTable columns={columns} data={rows} customStyles={DEFAULT_TABLE_CUSTOM_STYLES} pagination />
            )}
          </div>
        </div>
        <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
          <Form onSubmit={handleCreate}>
            <ModalHeader toggle={() => setModalOpen(false)}>Register fixed asset</ModalHeader>
            <ModalBody>
              <Row>
                <Col md="4"><FormGroup><Label>Asset tag</Label><Input required value={form.asset_tag} onChange={(e) => setForm({ ...form, asset_tag: e.target.value })} /></FormGroup></Col>
                <Col md="8"><FormGroup><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FormGroup></Col>
                <Col md="4"><FormGroup><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></FormGroup></Col>
                <Col md="4"><FormGroup><Label>Acquisition date</Label><Input type="date" required value={form.acquisition_date} onChange={(e) => setForm({ ...form, acquisition_date: e.target.value })} /></FormGroup></Col>
                <Col md="4"><FormGroup><Label>Cost</Label><Input type="number" min="0.01" step="0.01" required value={form.acquisition_cost} onChange={(e) => setForm({ ...form, acquisition_cost: e.target.value })} /></FormGroup></Col>
                <Col md="4"><FormGroup><Label>Salvage value</Label><Input type="number" min="0" value={form.salvage_value} onChange={(e) => setForm({ ...form, salvage_value: e.target.value })} /></FormGroup></Col>
                <Col md="4"><FormGroup><Label>Useful life (months)</Label><Input type="number" min="1" value={form.useful_life_months} onChange={(e) => setForm({ ...form, useful_life_months: e.target.value })} /></FormGroup></Col>
                <Col md="6"><FormGroup><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></FormGroup></Col>
                <Col md="6">
                  <BranchStaffSelect
                    staff={staff}
                    staffLoading={staffLoading}
                    value={form.custodian_user_id}
                    onChange={(e) => setForm({ ...form, custodian_user_id: e.target.value })}
                  />
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" type="button" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button color="primary" type="submit" disabled={saving}>Save</Button>
            </ModalFooter>
          </Form>
        </Modal>
      </div>
    </Fragment>
  );
};

export default FixedAssetsPage;
